var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Controller, FormProvider, useForm, } from 'react-hook-form';
import { Button, LoadingButton, ModalHeader, ModalBody, ModalFooter, TextField, Typography, } from '@one-thd/sui-atomic-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { CardType } from '../../../constants/payments';
import { getTextFieldStatus } from '../../../utils/form';
import { IMAGES_ASSETS_URL } from '../../../constants/links';
import { GiftCardLogoAndNumber } from '../gift-card/logo-number';
import { getFormattedKeytag } from '../../../utils/payments';
import { validationSchema } from './validation';
const headerMappings = {
    [CardType.GIFT_CARD]: () => {
        return 'Add/Edit Nickname';
    },
    [CardType.PAYPAL_ACCOUNT]: () => 'Add/Edit Nickname',
    [CardType.PRO_XTRA_DOLLARS]: () => 'Edit Nickname',
    [CardType.CHECKING_ACCOUNT]: () => 'Add/Edit Nickname',
    [CardType.KEY_TAG]: () => 'Add/Edit Nickname',
};
const buttonMappings = {
    [CardType.GIFT_CARD]: () => {
        return 'Save';
    },
    [CardType.PAYPAL_ACCOUNT]: () => 'Save',
    [CardType.PRO_XTRA_DOLLARS]: () => 'Save',
    [CardType.CHECKING_ACCOUNT]: () => 'Save',
    [CardType.KEY_TAG]: () => 'Save',
};
const fieldLabelMappings = {
    [CardType.GIFT_CARD]: () => {
        return 'Gift Card Nickname';
    },
    [CardType.PAYPAL_ACCOUNT]: () => 'PayPal Nickname',
    [CardType.PRO_XTRA_DOLLARS]: () => 'Nickname',
    [CardType.CHECKING_ACCOUNT]: () => 'Account Nickname',
    [CardType.KEY_TAG]: () => 'Keytag Nickname',
};
const childrenMapping = {
    [CardType.GIFT_CARD]: (card) => {
        return (React.createElement(Typography, { variant: "body-base" },
            React.createElement("div", { className: "sui-flex sui-flex-wrap sui-py-4" },
                React.createElement(GiftCardLogoAndNumber, { card: card }))));
    },
    [CardType.PAYPAL_ACCOUNT]: () => null,
    [CardType.PRO_XTRA_DOLLARS]: (card) => {
        const { cardNumberLast4, rewardExpDate } = card;
        return (React.createElement("div", { className: "sui-flex sui-mb-6" },
            React.createElement("img", { width: 40, height: "auto", className: "sui-inline-block sui-mr-4", src: `${IMAGES_ASSETS_URL}/pro-xtra-rewards.png`, alt: "ProXtra card logo" }),
            React.createElement("div", { className: "sui-mr-4" },
                React.createElement(Typography, { variant: "body-lg", color: "subtle" },
                    "-",
                    cardNumberLast4,
                    "-")),
            React.createElement(Typography, { variant: "body-lg", color: "subtle" }, `Exp ${rewardExpDate}`)));
    },
    [CardType.CHECKING_ACCOUNT]: (card) => {
        const { cardNumberLast4 } = card;
        return (React.createElement("div", { className: "sui-pb-6" },
            React.createElement(Typography, { variant: "body-lg" },
                "Account ****",
                cardNumberLast4)));
    },
    [CardType.KEY_TAG]: (card) => {
        return (React.createElement("div", { className: "sui-pb-6" },
            React.createElement(Typography, { variant: "body-lg" }, getFormattedKeytag(card))));
    },
};
export const EditCardNicknameModal = ({ card, cardType, hideModal, onConfirmEdit, isLoading, }) => {
    const defaultValues = {
        cardNickName: card.cardNickName || '',
    };
    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
    });
    const { control, formState: { errors: formErrors, dirtyFields }, handleSubmit, } = formMethods;
    const onSubmitForm = ({ cardNickName, }) => {
        if (dirtyFields.cardNickName) {
            onConfirmEdit(cardNickName || '');
        }
        else {
            hideModal();
        }
    };
    const headerText = headerMappings[cardType](card);
    const buttonText = buttonMappings[cardType](card);
    const labelText = fieldLabelMappings[cardType](card);
    const children = childrenMapping[cardType](card);
    return (React.createElement(FormProvider, Object.assign({}, formMethods),
        React.createElement("form", { onSubmit: handleSubmit(onSubmitForm) },
            React.createElement(ModalHeader, { onClose: hideModal }, headerText),
            React.createElement(ModalBody, null,
                children,
                React.createElement(Controller, { name: "cardNickName", control: control, render: ({ field }) => {
                        var _a;
                        const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                        return (React.createElement(TextField, { id: "tenderNickNameField", optional: true, fullWidth: true, label: labelText, type: "text", status: getTextFieldStatus(formErrors.cardNickName), statusMessage: (_a = formErrors.cardNickName) === null || _a === void 0 ? void 0 : _a.message, value: value || '', name: name, onChange: onChange, InputProps: {
                                inputProps: Object.assign(Object.assign({}, rest), { 'data-testid': 'nicknameField' }),
                            } }));
                    } })),
            React.createElement(ModalFooter, { position: "end" },
                React.createElement("div", { className: "sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto" },
                    React.createElement(Button, { variant: "secondary", onClick: hideModal }, "Cancel"),
                    React.createElement(LoadingButton, { loading: isLoading, variant: "primary", type: "submit", "data-testid": "submitBtn" }, buttonText))))));
};
EditCardNicknameModal.displayName = 'EditCardNicknameModal';
